import React, { forwardRef } from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@lib/utils';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Optional icon to display */
  icon?: LucideIcon;
  /** Position of the icon */
  iconPosition?: 'left' | 'right';
  /** Additional CSS classes for the icon */
  iconClassName?: string;
  /** Error state */
  error?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Label for the input */
  label?: string;
  /** Helper text below input */
  helperText?: string;
  /** Input variant style */
  variant?: 'outlined' | 'filled';
  /** Full width */
  fullWidth?: boolean;
  /** Input size */
  size?: 'small' | 'medium';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Input Atom Component
 * 
 * A fundamental form input element with optional icon and error states.
 * Designed to replace Material-UI TextField with similar API.
 * 
 * @example
 * ```tsx
 * // Basic input
 * <Input placeholder="Enter text..." />
 * 
 * // Input with label
 * <Input label="Email" type="email" />
 * 
 * // Input with icon
 * <Input icon={Search} placeholder="Search..." />
 * 
 * // Error state
 * <Input error errorMessage="This field is required" />
 * 
 * // Full width variant
 * <Input label="Full Name" fullWidth variant="outlined" />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>((
  {
    icon: Icon,
    iconPosition = 'left',
    iconClassName = '',
    error = false,
    errorMessage,
    label,
    helperText,
    variant = 'outlined',
    fullWidth = false,
    size = 'medium',
    className = '',
    ...props
  },
  ref
) => {
  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm',
    medium: 'px-3 py-2 text-sm',
  };
  
  const baseClasses = cn(
    'border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0',
    'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
    sizeClasses[size],
    fullWidth ? 'w-full' : ''
  );
  
  const variantClasses = {
    outlined: cn(
      'border-gray-300 bg-white hover:border-primary-400',
      error 
        ? 'border-red-500 focus:border-red-500 focus:ring-red-200' 
        : 'focus:border-primary-500 focus:ring-primary-200'
    ),
    filled: cn(
      'border-transparent bg-gray-50 hover:bg-gray-100',
      error
        ? 'bg-red-50 border-red-500 focus:ring-red-200'
        : 'focus:ring-primary-200'
    ),
  };

  const iconClasses = Icon 
    ? iconPosition === 'left' 
      ? 'pl-10' 
      : 'pr-10'
    : '';

  const inputClasses = cn(
    baseClasses,
    variantClasses[variant],
    iconClasses,
    className
  );

  return (
    <div className={cn('relative', fullWidth && 'w-full')}>
      {label && (
        <label className={cn(
          'block text-sm font-medium mb-1',
          error ? 'text-red-700' : 'text-gray-700'
        )}>
          {label}
        </label>
      )}
      
      <div className="relative">
        {Icon && (
          <div className={cn(
            'absolute inset-y-0 flex items-center pointer-events-none',
            iconPosition === 'left' ? 'left-0 pl-3' : 'right-0 pr-3'
          )}>
            <Icon className={cn(
              'w-4 h-4', 
              error ? 'text-red-400' : 'text-gray-400',
              iconClassName
            )} />
          </div>
        )}
        
        <input
          ref={ref}
          className={inputClasses}
          {...props}
        />
      </div>
      
      {(error && errorMessage) && (
        <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
      )}
      
      {(!error && helperText) && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
